import React, { Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconAnimated from '../IconAnimated/IconAnimated';

function sizeInput(theme, size, fieldValidation) {
  switch (size) {
    case 'px20':
      return `
                ${
                  fieldValidation
                    ? `padding: ${theme.spacing.px20} ${theme.spacing.px60} ${theme.spacing.px20} ${theme.spacing.px20};`
                    : `padding: ${theme.spacing.px20} ${theme.spacing.px20};`
                }
            `;

    case 'px15':
      return `
                padding: ${theme.spacing.px10} ${theme.spacing.px15};
            `;

    default:
      return `
                padding: ${theme.spacing.px10} ${theme.spacing.px15};
            `;
  }
}

const InputDefaultWrapper = styled('div')`
  ${({ theme, sizeWidth }) => `
        position: relative;
        background: ${theme.colors.white};
        border-radius: ${theme.rounded.px10};
        ${sizeWidth ? `width: ${sizeWidth};` : ''}
    `}
`;

const InputDefaultContent = styled('div')`
  ${({ theme, searchAbsolute, mobile }) => `
        ${
          searchAbsolute
            ? `
            background: ${theme.colors.white};
            position: absolute;
            right: 0;
            top: -19px;
            border-radius: ${theme.rounded.px10};
            `
            : ''
        }
        ${
          mobile
            ? `
                max-width: ${theme.spacing.full};
                background: transparent;
                width: ${theme.spacing.full};
                ${
                  searchAbsolute
                    ? `
                        position: static;
                    `
                    : ''
                }
            `
            : ''
        }
    `}
`;

const InputDefaultStyled = styled('input')`
  ${({
    theme,
    icon,
    autoResize,
    withAutoComplete,
    optionsAutoComplete,
    block,
    size,
    fontSize,
    fieldValidation,
    bgColor,
    typed,
    withoutBorder,
  }) => `
    border: 1px solid ${theme.colors.grey2};
    font-size: ${theme.fontSize[fontSize]};
    
    font-weight: ${theme.fontWeight.light};
    color: ${theme.colors.blueGrey2};
    background: none;
    outline: none;
    box-sizing: border-box;
    transition: width 0.5s;
    position: relative;
    &:-webkit-autofill,
    &:-webkit-autofill:hover, 
    &:-webkit-autofill:focus, 
    &:-webkit-autofill:active  {
        -webkit-box-shadow: 0 0 0 30px white inset !important;
    }
    &:disabled {
        background: ${theme.colors.blue5};
        color: ${theme.colors.blueGrey3};
        cursor: not-allowed;
    }
    ${bgColor ? `background-color: ${theme.colors[bgColor]}; border: none; color: black;` : ''}  
    ${
      withAutoComplete && optionsAutoComplete && typed && typed.length
        ? `
        border-radius: ${theme.rounded.px10};
        &:active,
        &:focus,
        &:focus-within {
            border-radius: ${theme.rounded.px10} ${theme.rounded.px10} ${theme.rounded.none} ${theme.rounded.none};
        }
        `
        : `
        border-radius: ${theme.rounded.px10};
        `
    }
    ${
      icon
        ? `padding: ${theme.spacing.px10} ${theme.spacing.px15} ${theme.spacing.px10} ${theme.spacing.px40};`
        : sizeInput(theme, size, fieldValidation)
    }
    ${
      autoResize
        ? `
        width: 210px;
        &:active,
        &:focus,
        &:focus-within {
            width: 400px;
            max-width: ${theme.spacing.full};
        }
        `
        : `
        width: ${theme.spacing.full};
        max-width: ${theme.spacing.full};
        `
    }
    ${
      block
        ? `
        width: ${theme.spacing.full};
        max-width: ${theme.spacing.full};
        `
        : ''
    }
    ${
      withoutBorder
        ? `
        border: none;
        `
        : ''
    }
`}
`;

const CustomIcon = styled(FontAwesomeIcon)`
  ${({ theme }) => `
    font-size: ${theme.fontSize.px14};
    color: ${theme.colors.blueGrey3};
    position: absolute;
    left: ${theme.spacing.px15};
    top: 13px;
`}
`;

const CustomValidation = styled('span')`
  ${({ theme }) => `
    position: absolute;
    right: ${theme.spacing.px20};
    top: ${theme.spacing.px20};
`}
`;

const CustomValidationIcon = styled(FontAwesomeIcon)`
  ${({ theme, color }) => `
    display: block;
    font-size: ${theme.fontSize.px25};
    color: ${theme.colors[color]};
`}
`;

const MessageValidation = styled('div')`
  ${({ theme }) => `
    font-size: ${theme.fontSize.px12};
    color: ${theme.colors.red3};
    margin-top: ${theme.spacing.px3};
`}
`;

export class InputDefault extends Component {
  state = {
    value: '',
  };

  static propTypes = {
    icon: PropTypes.array,
    autoResize: PropTypes.bool,
    withAutoComplete: PropTypes.bool,
    type: PropTypes.string,
    id: PropTypes.node,
    name: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyUp: PropTypes.func,
    searchAbsolute: PropTypes.bool,
    size: PropTypes.string,
    fontSize: PropTypes.string,
    mobile: PropTypes.bool,
    withoutBorder: PropTypes.bool,
  };

  static defaultProps = {
    icon: [],
    id: 'inputDefault',
    name: 'inputDefault',
    value: undefined,
    autoResize: false,
    withAutoComplete: false,
    type: 'text',
    searchAbsolute: false,
    size: 'px15',
    fontSize: 'px14',
    mobile: false,
    withoutBorder: false,
  };

  render() {
    const {
      props: {
        autoResize,
        bgColor,
        block,
        data,
        disabled,
        errors,
        fieldLoading,
        fieldValidation,
        fontSize,
        icon,
        id,
        innerRef,
        mobile,
        name,
        onBlur,
        onChange,
        onFocus,
        onKeyUp,
        searchAbsolute,
        size,
        sizeWidth,
        type,
        typed,
        value,
        withAutoComplete,
        withoutBorder,
        ...rest
      },
    } = this;

    return (
      <InputDefaultWrapper sizeWidth={sizeWidth}>
        <InputDefaultContent searchAbsolute={searchAbsolute} mobile={mobile}>
          {icon && icon.length ? (
            <CustomIcon icon={icon} spin={icon[1] === 'circle-notch'} />
          ) : null}
          <InputDefaultStyled
            bgColor={bgColor}
            icon={icon && icon.length}
            autoResize={autoResize}
            withAutoComplete={withAutoComplete}
            type={type}
            id={id}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyUp={onKeyUp}
            optionsAutoComplete={data}
            disabled={disabled}
            block={block}
            size={size}
            fontSize={fontSize}
            ref={innerRef}
            fieldValidation={fieldValidation}
            typed={typed}
            withoutBorder={withoutBorder}
            {...rest}
          />
          {fieldValidation ? (
            (!!errors && errors.length >= 1) || value.length === 0 ? (
              <CustomValidation color={fieldLoading ? 'grey2' : 'green3'}>
                <CustomValidationIcon color="red3" icon={['fal', 'times']} />
              </CustomValidation>
            ) : (
              <CustomValidation>
                {fieldLoading ? (
                  <CustomValidationIcon color="grey2" icon={['fal', 'circle-notch']} spin />
                ) : (
                  <IconAnimated />
                )}
              </CustomValidation>
            )
          ) : null}
        </InputDefaultContent>
        {errors ? <MessageValidation>{errors}</MessageValidation> : null}
      </InputDefaultWrapper>
    );
  }
}

export default InputDefault;
